import * as React from "react";
import { useState, useCallback } from "react";
import * as Immutable from "immutable";
import styled from "styled-components";
import { useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import PaginatedItemOverview, { DEFAULT_PAGINATION } from "components/common/PaginatedItemOverview";
import SectionComponent from "components/common/Section/SectionComponent";
import RolesSelector from "components/permissions/RolesSelector";
import RolesQueryHelp from "components/users/RolesQueryHelp";
import TeamsDomain from "../../domainActions/TeamsDomain";
import { teamQueryKey } from "../../hooks/useTeam";
import { TEAM_MEMBERS_BY_ROLE_QUERY_KEY } from "../../logic/useTeamMembersByRole";
const Container = styled.div`
  margin-top: 15px;
  margin-bottom: 15px;
`;
const _updateTeam = (team, { roles }) => {
  const newTeam = team.toBuilder().roles(Immutable.Set(roles)).build();
  return TeamsDomain.update(newTeam);
};
const RolesSection = ({ team }) => {
  const { t } = useTranslation("enterprise_security");
  const [loading, setLoading] = useState(false);
  const [paginatedRoles, setPaginatedRoles] = useState();
  const queryClient = useQueryClient();
  const _onLoad = useCallback(
    (pagination = DEFAULT_PAGINATION) => {
      setLoading(true);
      return TeamsDomain.loadRolesForTeam(team.id, team.name, pagination).then((newPaginatedRoles) => {
        setLoading(false);
        return newPaginatedRoles;
      });
    },
    [team.id, team.name]
  );
  const onRolesUpdate = (data) => _updateTeam(team, data).then(() => {
    queryClient.invalidateQueries({ queryKey: teamQueryKey(team.id) });
    queryClient.invalidateQueries({ queryKey: TEAM_MEMBERS_BY_ROLE_QUERY_KEY });
    return _onLoad().then(setPaginatedRoles);
  });
  const _onAssignRole = (newRoles) => {
    const teamRoles = team.roles;
    const newRoleIds = teamRoles.union(newRoles.map((r) => r.id)).toJS();
    return onRolesUpdate({ roles: newRoleIds });
  };
  const onDeleteRole = (role) => {
    const newUserRoles = team.roles.remove(role.id).toJS();
    if (window.confirm(t("team_edit_roles.confirm_remove", { name: role.name }))) {
      onRolesUpdate({ roles: newUserRoles });
    }
  };
  return /* @__PURE__ */ React.createElement(SectionComponent, { title: t("team_edit_roles.title"), showLoading: loading }, /* @__PURE__ */ React.createElement("h3", null, t("team_edit_roles.assign_roles")), /* @__PURE__ */ React.createElement(Container, null, /* @__PURE__ */ React.createElement(RolesSelector, { onSubmit: _onAssignRole, assignedRolesIds: team.roles })), /* @__PURE__ */ React.createElement("h3", null, t("team_edit_roles.selected_roles")), /* @__PURE__ */ React.createElement(Container, null, /* @__PURE__ */ React.createElement(
    PaginatedItemOverview,
    {
      noDataText: t("team_edit_roles.no_data"),
      onLoad: _onLoad,
      overrideList: paginatedRoles,
      onDeleteItem: onDeleteRole,
      queryHelper: /* @__PURE__ */ React.createElement(RolesQueryHelp, null)
    }
  )));
};
export default RolesSection;
